/**
 * 塊 1 — 所見（Screen Contract「塊 1」・State Matrix I 群・Experience Spec U-3〜U-8 / U-24〜U-28）。
 *
 * 所見は下の期間の選択に連動しない（U-4・I13）。表とは別に取得し、片方の遅れ・失敗でもう片方を待たせない（L5）。
 * 根拠の数字は所見を作るときに集計から差し込まれたもので、ラベル（W-10）は workspace 数から決まる（API 側）。
 * 再生成・Slack への再送のボタンは作らない（X8）。
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  FeatureUsageFetchError,
  fetchUsageInsight,
  type UsageInsightItem,
  type UsageTab,
} from '../../lib/feature-usage-api';
import { CopyButton } from './CopyButton';
import { dayWithWeekday } from './format';

const VISIBLE_ITEMS = 3;

export function evidenceLine(item: UsageInsightItem): string {
  const e = item.evidence;
  const segment = e.segment === 'alys' ? '自社' : '顧客';
  const n = e.segment === 'alys' ? 1 : e.customerWorkspaces;
  return `根拠: ${e.target} ・ ${e.counts} ・ ${segment} ${n} workspace ・ ${e.fromDay}〜${e.toDay}`;
}

function ItemView({
  item,
  onOpenTable,
}: {
  item: UsageInsightItem;
  onOpenTable: (link: {
    fromDay: string;
    toDay: string;
    tab: UsageTab;
    workspaceId: string | null;
  }) => void;
}) {
  return (
    <li className="py-2">
      <div className="flex gap-3">
        <span data-testid="insight-kind" className="w-16 shrink-0 text-xs text-slate-500">
          {item.kind === 'observation' ? '観察' : '改善候補'}
        </span>
        <div className="min-w-0 flex-1 text-sm">
          <p className="text-slate-900">{item.text}</p>
          <p className="mt-1 text-xs tabular-nums text-slate-600">{evidenceLine(item)}</p>
          <div className="mt-1 flex items-center gap-4 text-xs">
            <span className="text-slate-500">{item.label}</span>
            <button
              type="button"
              className="text-slate-600 underline"
              onClick={() =>
                onOpenTable({
                  fromDay: item.evidence.fromDay,
                  toDay: item.evidence.toDay,
                  tab: item.evidence.link.tab,
                  workspaceId: item.evidence.link.workspaceId,
                })
              }
            >
              表で見る
            </button>
            {item.kind === 'candidate' && (
              <CopyButton
                label="feature-gate 用にコピー"
                text={[item.text, evidenceLine(item), item.label].join('\n')}
              />
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function ItemGroup({
  items,
  onOpenTable,
}: {
  items: UsageInsightItem[];
  onOpenTable: Parameters<typeof ItemView>[0]['onOpenTable'];
}) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return null;
  const shown = expanded ? items : items.slice(0, VISIBLE_ITEMS);
  return (
    <>
      {shown.map((item, i) => (
        <ItemView key={`${item.kind}-${i}`} item={item} onOpenTable={onOpenTable} />
      ))}
      {!expanded && items.length > VISIBLE_ITEMS && (
        <li>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-xs text-slate-600 underline"
          >
            ほか {items.length - VISIBLE_ITEMS} 件を表示
          </button>
        </li>
      )}
    </>
  );
}

export function InsightPanel({
  apiBaseUrl,
  day,
  onDayChange,
  onOpenTable,
}: {
  apiBaseUrl: string;
  day: string | null;
  onDayChange: (day: string) => void;
  onOpenTable: Parameters<typeof ItemView>[0]['onOpenTable'];
}) {
  const query = useQuery({
    queryKey: ['admin-ops-usage-insight', day],
    queryFn: () => fetchUsageInsight(apiBaseUrl, day),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const data = query.data?.configured ? query.data : null;
  const errorKind =
    query.error instanceof FeatureUsageFetchError
      ? query.error.kind
      : query.error
        ? 'failed'
        : null;

  if (query.data && !query.data.configured) return null; // 未配線は表の側で U-31 を出す

  return (
    <section
      data-testid="feature-usage-insight"
      className="mb-6 rounded-lg border border-slate-200 bg-white p-4"
      aria-busy={query.isFetching}
    >
      {errorKind && (
        <p className="text-sm text-rose-600">
          {errorKind === 'permission_denied'
            ? '機能利用の記録を読む権限がありません（alys_ops_readonly に SELECT 権限が必要です）。'
            : '所見を取得できませんでした。もう一度お試しください。'}
          {errorKind === 'failed' && (
            <button type="button" onClick={() => void query.refetch()} className="ml-2 underline">
              再試行
            </button>
          )}
        </p>
      )}

      {!errorKind && query.isLoading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-5 animate-pulse rounded bg-slate-200/60" />
          ))}
        </div>
      )}

      {!errorKind && data && (
        <div className={query.isPlaceholderData ? 'opacity-50' : undefined}>
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-slate-900">
              {dayWithWeekday(data.day)}の所見
            </h2>
            <button
              type="button"
              onClick={() => onDayChange(data.previousDay)}
              className="text-xs text-slate-600 underline"
            >
              前の日
            </button>
            <button
              type="button"
              disabled={data.nextDay === null}
              onClick={() => data.nextDay && onDayChange(data.nextDay)}
              className="text-xs text-slate-600 underline disabled:text-slate-300 disabled:no-underline"
            >
              次の日
            </button>
            {data.insight && (
              <span className="ml-auto text-xs text-slate-500">
                {data.insight.slackSentAt ? 'Slack: 送信済み' : 'Slack: 未送信'}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            前日と直近 7 日を、その前の 7
            日と比べて自動で作成しています。下の期間の選択には連動しません。
          </p>
          <InsightBody data={data} onOpenTable={onOpenTable} />
        </div>
      )}
    </section>
  );
}

function InsightBody({
  data,
  onOpenTable,
}: {
  data: Extract<Awaited<ReturnType<typeof fetchUsageInsight>>, { configured: true }>;
  onOpenTable: Parameters<typeof ItemView>[0]['onOpenTable'];
}) {
  const insight = data.insight;
  if (!insight) {
    return (
      <p className="mt-3 text-sm text-slate-600">
        この日の所見はまだ作成されていません（毎朝 {data.scheduledAt} 頃に作成します）。
      </p>
    );
  }
  if (insight.status === 'failed' || insight.status === 'input_rejected') {
    return (
      <p className="mt-3 text-sm text-slate-600">
        この日の所見は作成できませんでした。表の数字は下で確認できます。
      </p>
    );
  }
  const made = insight.items.length + insight.droppedCount;
  const dropped =
    insight.droppedCount > 0 ? (
      <p className="mt-2 text-xs text-slate-500">
        作成された {made} 項目のうち {insight.droppedCount}{' '}
        項目は、根拠を集計で確かめられなかったため表示していません。
      </p>
    ) : null;
  if (insight.items.length === 0) {
    // すべて落とした日（I7）は「変化なし」と言わない。
    return (
      dropped ?? <p className="mt-3 text-sm text-slate-600">この日は変化がありませんでした。</p>
    );
  }
  return (
    <>
      <ul className="mt-2 divide-y divide-slate-100">
        <ItemGroup
          items={insight.items.filter((i) => i.kind === 'observation')}
          onOpenTable={onOpenTable}
        />
        <ItemGroup
          items={insight.items.filter((i) => i.kind === 'candidate')}
          onOpenTable={onOpenTable}
        />
      </ul>
      {dropped}
    </>
  );
}
